import { useCallback, useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { Provider } from '@atlaskit/collab-provider';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';

import type { PresenceParticipant } from '../presence-store';
import type { PresenceContentMode, PresencePageContentType } from '../presenceTypes';

//TODO dynamic configs https://product-fabric.atlassian.net/browse/MDS-1207
const SESSION_LIMIT = 50;
const TOTAL_SESSION_LIMIT = 1000;

type Props = {
	contentId: string;
	contentMode: PresenceContentMode;
	contentType: PresencePageContentType;
	participants: Map<string, PresenceParticipant>;
	provider: Provider | null;
};

export const usePresenceLimiter = ({
	contentId,
	contentMode,
	contentType,
	participants,
	provider,
}: Props) => {
	const { userId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const getCurrentUser = useCallback(() => {
		return participants.get(userId ?? 'unidentified');
	}, [participants, userId]);

	const getTotalSessions = useCallback(() => {
		return provider?.getParticipants().length ?? 0;
	}, [provider]);

	const isViewContentType = useCallback(() => {
		return ['blogpost', 'page'].includes(contentType) && contentMode === 'view';
	}, [contentType, contentMode]);

	const checkSessionLimit = useCallback(
		(currentUser: PresenceParticipant | undefined, totalSessions: number) => {
			const currentUserHasReachedSessionLimit =
				currentUser && Object.keys(currentUser.sessions).length > SESSION_LIMIT;

			if (currentUserHasReachedSessionLimit || totalSessions > TOTAL_SESSION_LIMIT) {
				provider?.destroy();

				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						source: 'presenceAvatarGroup',
						action: 'destroyConnection',
						actionSubject: 'presenceAvatarGroupConnection',
						attributes: {
							sessionLimitType: currentUserHasReachedSessionLimit ? 'currentUser' : 'totalSessions',
							contentId,
							contentType,
						},
					},
				}).fire();
			}
		},
		[contentId, contentType, createAnalyticsEvent, provider],
	);

	useEffect(() => {
		// We only want to apply the limiter for blogs and pages, and only when in view mode.
		if (!isViewContentType()) {
			return;
		}

		checkSessionLimit(getCurrentUser(), getTotalSessions());
	}, [checkSessionLimit, getCurrentUser, getTotalSessions, isViewContentType]);

	return {};
};
